/* A small GIF89a encoder for replay exports, with no dependencies.

   Screen recordings change little from one frame to the next, so every frame
   after the first carries only the rectangle that changed, with the pixels
   that didn't left transparent over the frame before. Each frame gets its own
   palette (median cut over the pixels it carries, in a local color table),
   which keeps flat UI colors close to exact, and a few colors the caller
   names (the replay's markers) are always kept exact. A frame that changes
   nothing only lengthens the one before it. */

/** A pixel whose channels each moved at most this much since it was last written counts as unchanged: JPEG noise. */
const SAME = 6;
const MAX_DELAY_CS = 65535;

/** A growable byte buffer. */
class Bytes {
  private buf = new Uint8Array(1 << 16);
  length = 0;

  private room(n: number): void {
    if (this.length + n <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.length + n) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.buf.subarray(0, this.length));
    this.buf = next;
  }

  byte(b: number): void {
    this.room(1);
    this.buf[this.length++] = b;
  }

  word(w: number): void {
    this.room(2);
    this.buf[this.length++] = w & 0xff;
    this.buf[this.length++] = (w >> 8) & 0xff;
  }

  bytes(src: Uint8Array): void {
    this.room(src.length);
    this.buf.set(src, this.length);
    this.length += src.length;
  }

  ascii(text: string): void {
    for (let i = 0; i < text.length; i++) this.byte(text.charCodeAt(i));
  }

  view(): Uint8Array {
    return this.buf.subarray(0, this.length);
  }
}

/* ------------------------------------------------------------- palette */

const BINS = 1 << 15;
const bin = (r: number, g: number, b: number) => ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);

interface Box {
  bins: number[];
  pixels: number;
  /** The widest channel (0 red, 1 green, 2 blue) and its spread, in 5-bit steps. */
  channel: number;
  spread: number;
}

const channelOf = (b: number, c: number) => (c === 0 ? b >> 10 : c === 1 ? (b >> 5) & 31 : b & 31);

function makeBox(bins: number[], count: Uint32Array): Box {
  const lo = [31, 31, 31];
  const hi = [0, 0, 0];
  let pixels = 0;
  for (const b of bins) {
    pixels += count[b];
    for (let c = 0; c < 3; c++) {
      const v = channelOf(b, c);
      if (v < lo[c]) lo[c] = v;
      if (v > hi[c]) hi[c] = v;
    }
  }
  let channel = 0;
  for (let c = 1; c < 3; c++) if (hi[c] - lo[c] > hi[channel] - lo[channel]) channel = c;
  return { bins, pixels, channel, spread: hi[channel] - lo[channel] };
}

/** Scratch space for one frame's histogram, kept between frames. */
class Histogram {
  readonly count = new Uint32Array(BINS);
  readonly r = new Float64Array(BINS);
  readonly g = new Float64Array(BINS);
  readonly b = new Float64Array(BINS);
  readonly lut = new Uint8Array(BINS);

  clear(): void {
    this.count.fill(0);
    this.r.fill(0);
    this.g.fill(0);
    this.b.fill(0);
  }
}

/**
 * Median cut over the histogram: at most `max` colors as RGB triples, and
 * `lut` filled with each used bin's palette index. `keep` colors are added
 * to the palette whenever a bin is closer to one of them than to its box.
 */
function quantize(h: Histogram, max: number, keep: readonly (readonly [number, number, number])[]): Uint8Array {
  const used: number[] = [];
  for (let b = 0; b < BINS; b++) if (h.count[b]) used.push(b);
  const room = Math.max(1, max - keep.length);
  const boxes: Box[] = [makeBox(used, h.count)];
  while (boxes.length < room) {
    // Split the box with the most pixels times spread: busy regions get the colors.
    let best = -1;
    let score = 0;
    for (let i = 0; i < boxes.length; i++) {
      const s = boxes[i].bins.length > 1 ? boxes[i].pixels * (boxes[i].spread + 1) : 0;
      if (s > score) {
        score = s;
        best = i;
      }
    }
    if (best < 0) break;
    const box = boxes[best];
    const c = box.channel;
    box.bins.sort((x, y) => channelOf(x, c) - channelOf(y, c));
    let acc = 0;
    let cut = 0;
    while (cut < box.bins.length - 2 && acc + h.count[box.bins[cut]] < box.pixels / 2) acc += h.count[box.bins[cut++]];
    boxes.splice(best, 1, makeBox(box.bins.slice(0, cut + 1), h.count), makeBox(box.bins.slice(cut + 1), h.count));
  }

  const colors: number[] = [];
  boxes.forEach((box, index) => {
    let n = 0;
    let r = 0;
    let g = 0;
    let b = 0;
    for (const x of box.bins) {
      n += h.count[x];
      r += h.r[x];
      g += h.g[x];
      b += h.b[x];
    }
    colors.push(Math.round(r / n), Math.round(g / n), Math.round(b / n));
    for (const x of box.bins) h.lut[x] = index;
  });

  // The kept colors: a bin nearer one of them than to its box's color uses it.
  if (keep.length) {
    const kept: number[] = [];
    for (const x of used) {
      const n = h.count[x];
      const r = h.r[x] / n;
      const g = h.g[x] / n;
      const b = h.b[x] / n;
      const i = h.lut[x];
      let bestD = (r - colors[i * 3]) ** 2 + (g - colors[i * 3 + 1]) ** 2 + (b - colors[i * 3 + 2]) ** 2;
      keep.forEach((k, j) => {
        const d = (r - k[0]) ** 2 + (g - k[1]) ** 2 + (b - k[2]) ** 2;
        if (d < bestD) {
          bestD = d;
          if (kept[j] === undefined) {
            kept[j] = colors.length / 3;
            colors.push(k[0], k[1], k[2]);
          }
          h.lut[x] = kept[j];
        }
      });
    }
  }
  return Uint8Array.from(colors);
}

/* ----------------------------------------------------------------- LZW */

/** GIF's variable-width LZW, its code table stamped per reset so clearing it is free. */
class Lzw {
  private readonly codes = new Int32Array(4096 * 256);
  private readonly stamps = new Int32Array(4096 * 256);
  private stamp = 0;

  encode(indices: Uint8Array, minCodeSize: number, out: Bytes): void {
    out.byte(minCodeSize);
    const clear = 1 << minCodeSize;
    const eoi = clear + 1;
    let size = minCodeSize + 1;
    let next = eoi + 1;
    let cur = 0;
    let shift = 0;
    const block = new Uint8Array(255);
    let fill = 0;
    const put = (byte: number) => {
      block[fill++] = byte;
      if (fill === 255) {
        out.byte(255);
        out.bytes(block);
        fill = 0;
      }
    };
    const emit = (code: number) => {
      cur |= code << shift;
      shift += size;
      while (shift >= 8) {
        put(cur & 0xff);
        cur >>>= 8;
        shift -= 8;
      }
    };

    this.stamp++;
    emit(clear);
    let prefix = indices[0];
    for (let i = 1; i < indices.length; i++) {
      const k = indices[i];
      const key = (prefix << 8) | k;
      if (this.stamps[key] === this.stamp) {
        prefix = this.codes[key];
        continue;
      }
      emit(prefix);
      if (next === 4096) {
        emit(clear);
        next = eoi + 1;
        size = minCodeSize + 1;
        this.stamp++;
      } else {
        // The decoder widens its codes once the table outgrows them; widen in step.
        if (next >= 1 << size) size++;
        this.stamps[key] = this.stamp;
        this.codes[key] = next++;
      }
      prefix = k;
    }
    emit(prefix);
    emit(eoi);
    if (shift > 0) put(cur & 0xff);
    if (fill > 0) {
      out.byte(fill);
      out.bytes(block.subarray(0, fill));
    }
    out.byte(0);
  }
}

/* ------------------------------------------------------------- encoder */

interface Pending {
  image: Uint8Array;
  delayMs: number;
  /** The transparent index, or -1 for none. */
  transparent: number;
}

export class GifEncoder {
  private readonly out = new Bytes();
  private readonly lzw = new Lzw();
  private readonly hist = new Histogram();
  private readonly changed: Uint8Array;
  /** What each pixel last showed, RGB; null until the first frame. */
  private shown: Uint8Array | null = null;
  /** The last frame, held until the next tells how long it stays. */
  private pending: Pending | null = null;

  constructor(
    readonly width: number,
    readonly height: number,
    private readonly keep: readonly (readonly [number, number, number])[] = [],
  ) {
    this.changed = new Uint8Array(width * height);
    const o = this.out;
    o.ascii('GIF89a');
    o.word(width);
    o.word(height);
    o.byte(0x70); // no global color table; 8 bits of color resolution
    o.byte(0);
    o.byte(0);
    // NETSCAPE2.0: loop forever.
    o.byte(0x21);
    o.byte(0xff);
    o.byte(11);
    o.ascii('NETSCAPE2.0');
    o.byte(3);
    o.byte(1);
    o.word(0);
    o.byte(0);
  }

  /** Adds a frame of RGBA pixels, width × height, shown for `delayMs`. */
  addFrame(rgba: Uint8ClampedArray, delayMs: number): void {
    const { width: w, height: h, changed, hist } = this;
    const first = this.shown === null;
    const shown = this.shown ?? new Uint8Array(w * h * 3);
    this.shown = shown;

    // What changed, and the rectangle around it.
    let x0 = w;
    let y0 = h;
    let x1 = -1;
    let y1 = -1;
    hist.clear();
    for (let y = 0, p = 0; y < h; y++) {
      for (let x = 0; x < w; x++, p++) {
        const i = p * 4;
        const j = p * 3;
        const r = rgba[i];
        const g = rgba[i + 1];
        const b = rgba[i + 2];
        if (first || Math.abs(r - shown[j]) > SAME || Math.abs(g - shown[j + 1]) > SAME || Math.abs(b - shown[j + 2]) > SAME) {
          changed[p] = 1;
          const k = bin(r, g, b);
          hist.count[k]++;
          hist.r[k] += r;
          hist.g[k] += g;
          hist.b[k] += b;
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        } else {
          changed[p] = 0;
        }
      }
    }
    if (x1 < 0) {
      if (this.pending) this.pending.delayMs += delayMs;
      return;
    }

    const palette = quantize(hist, first ? 256 : 255, this.keep);
    const colors = palette.length / 3;
    // Index `colors`, the slot after the palette, is transparent: "as before".
    const transparent = first ? -1 : colors;
    let bits = 1;
    while (1 << bits < colors + (first ? 0 : 1)) bits++;

    const rw = x1 - x0 + 1;
    const rh = y1 - y0 + 1;
    const indices = new Uint8Array(rw * rh);
    for (let y = 0, k = 0; y < rh; y++) {
      for (let x = 0, p = (y0 + y) * w + x0; x < rw; x++, k++, p++) {
        if (!changed[p]) {
          indices[k] = transparent;
          continue;
        }
        const i = p * 4;
        const j = p * 3;
        indices[k] = hist.lut[bin(rgba[i], rgba[i + 1], rgba[i + 2])];
        shown[j] = rgba[i];
        shown[j + 1] = rgba[i + 1];
        shown[j + 2] = rgba[i + 2];
      }
    }

    const image = new Bytes();
    image.byte(0x2c);
    image.word(x0);
    image.word(y0);
    image.word(rw);
    image.word(rh);
    image.byte(0x80 | (bits - 1)); // a local color table of 2^bits colors
    const table = new Uint8Array(3 << bits);
    table.set(palette);
    image.bytes(table);
    this.lzw.encode(indices, Math.max(2, bits), image);

    this.flush();
    this.pending = { image: image.view().slice(), delayMs, transparent };
  }

  private flush(): void {
    const p = this.pending;
    if (!p) return;
    const o = this.out;
    // Graphic control: keep this frame under the next (disposal 1), its delay, its transparent index.
    o.byte(0x21);
    o.byte(0xf9);
    o.byte(4);
    o.byte((1 << 2) | (p.transparent >= 0 ? 1 : 0));
    o.word(Math.min(MAX_DELAY_CS, Math.max(2, Math.round(p.delayMs / 10))));
    o.byte(p.transparent >= 0 ? p.transparent : 0);
    o.byte(0);
    o.bytes(p.image);
    this.pending = null;
  }

  /** The finished file. */
  finish(): Blob {
    this.flush();
    this.out.byte(0x3b);
    return new Blob([this.out.view().slice()], { type: 'image/gif' });
  }
}
